import { Component, OnInit } from "@angular/core";

import { User, SamlAlias, Resource } from "@impulse/sdp-shared-models";

import { PolicyWizardService } from "../policy-wizard.service";
import { ApiGatewayService } from "../../../../common/api-gateway/api-gateway.service";
import { SnackbarService } from "../../../../common/snackbar/snackbar.service";
import { TranslateService } from "../../../../common/translate/translate.service";
import { AbstractWizardStepComponent } from "app/administrative/wizard-dialog/abstract-wizard-step.component";
import { PolicyResourceSelectComponent } from "../policy-resource-select/policy-resource-select.component";
import { shortenPhrase } from "../../../../util";
import { SdpIcon } from "app/common/icon/models";
import { SamlAliasCreateComponent } from "../saml-alias-create/saml-alias-create.component";
import { AccessSelection } from "../models";

@Component({
	templateUrl: "./policy-access-select.component.html"
})
export class PolicyAccessSelectComponent extends AbstractWizardStepComponent<void> implements OnInit {
	public readonly iconUser: SdpIcon.Icon = { type: "material", name: "person", cssClass: "sdp-policies-type-icon" };
	public readonly iconSaml: SdpIcon.Icon = { type: "fa", name: "users", cssClass: "sdp-policies-type-icon" };
	public readonly addIcon: SdpIcon.Icon = {
		type: "material",
		name: "add",
		cssClass: "sdp-icon-button-icon sdp-policy-create-samlalias-button"
	};
	public shortenPhrase = shortenPhrase;

	// Raw data from the API
	private userData!: User[];
	private aliasData!: SamlAlias[];

	public accessData!: AccessSelection[];
	public selectedAccessData: AccessSelection[] = [];
	public dataLoaded = false;
	public hasIdp = false;

	private _nextStep: AbstractWizardStepComponent.ConstructorFunction<void> | undefined = undefined;

	constructor(
		private service: PolicyWizardService,
		private apiGatewayService: ApiGatewayService,
		private snackbarService: SnackbarService,
		private translateService: TranslateService
	) {
		super();
	}

	public async ngOnInit() {
		try {
			// Fetch all users and saml aliases from the API
			[this.userData, this.aliasData] = await Promise.all([
				this.apiGatewayService.getAllUsers(),
				this.apiGatewayService.getAllSamlAliases()
			]);
			// Now map users and aliases into the AccessSelection interface to populate the selections
			this.accessData = this.userData.map<AccessSelection>((user) => {
				return {
					type: "user",
					id: user.email,
					label: user.email
				};
			}).concat(this.aliasData.map<AccessSelection>((alias) => {
				return {
					type: "alias",
					id: alias.id,
					label: alias.label
				};
			}));
			// Now populate the selected AccessSelection with any access already granted
			if (!!this.service.resource) {
				if (this.service.selectedAccessData) {
					this.loadState();
				} else {
					this.selectedAccessData = this.accessData.filter((access) => {
						if (access.type === "user") {
							// Resource must be defined at this point, just making the type-checker happy
							return this.service.resource!.access.users.indexOf(access.id) !== -1;
						} else {
							return this.service.resource!.access.samlAliases.indexOf(access.id) !== -1;
						}
					});
				}
			}
			// Fetch the list of idps from the API
			const idps = await this.apiGatewayService.getIdentityProviders();
			this.hasIdp = idps.length > 0;
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		} finally {
			this.dataLoaded = true;
		}
	}

	public async showCreateSamlAlias() {
		this._nextStep = SamlAliasCreateComponent;
		await this.wizard.goNext();
	}

	public get title() {
		// If they navigate directly here without having first selected an application, we need to make sure we don't throw a null pointer
		// exception. If we do throw such an exception, the code that navigates back to a safe area will fail.
		const args = {
			applicationName: ""
		};
		if (!!this.service.resource) {
			args.applicationName = this.service.resource.name;
		}
		return this.translateService.translate("policy.wizard.groupSelect.title", args);
	}

	public get description(): string {
		return "policy.wizard.groupSelect.info";
	}

	public get nextStep() {
		return this._nextStep;
	}

	/**
	 * When creating a policy users may return to the resource selection component, when editing they may not
	 */
	public get previousStep() {
		if (this.service.isCreate) {
			return PolicyResourceSelectComponent;
		} else {
			return undefined;
		}
	}

	/**
	 * This is no form here, and input can't be invalid
	 */
	public get isFormValid() {
		return true;
	}

	public get showProgressBar() {
		return !this.dataLoaded;
	}

	public async performNext() {
		if (!this.nextStep) {
			// No next step defined, go ahead with policy update
			const newAccess: Resource.Access = {
				samlAliases: this.selectedAccessData
					.filter((d) => d.type === "alias")
					.map((d) => d.id),
				users: this.selectedAccessData
					.filter((d) => d.type === "user")
					.map((d) => d.id)
			};
			try {
				await this.service.updatePolicy(this.service.resource!.access, newAccess, this.service.resource!.urlName);
				this.service.closeDialog();
				this.snackbarService.open(
					this.translateService.translate(
						this.service.isCreate ? "policy.add.success" : "policy.update.success",
						{ name: this.service.resource!.name }
					)
				);
			} catch (err) {
				this.snackbarService.open(this.translateService.translate(err));
			}
		} else {
			// Next step defined, this means we're going to create a saml alias
			this.saveState();
		}
	}

	public performPrevious() {
		// NOOP
	}

	private saveState() {
		this.service.selectedAccessData = this.selectedAccessData;
	}

	private loadState() {
		if (!!this.service.selectedAccessData) {
			this.selectedAccessData = this.service.selectedAccessData;
		}
	}
}
